import { cn } from '@eversports/klimt-design-components/tailwind/utils'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

const buttonVariants = cva(
  'ring-offset-surface-background focus-visible:ring-border-divider rounded-md py-0.5 font-medium inline-flex items-center justify-center transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary:
          'bg-surface-brand-primary hover:bg-surface-brand-hover disabled:bg-surface-disabled disabled:text-text-disabled text-white disabled:opacity-100',
        secondary:
          'border-border-brand-primary text-text-brand-primary hover:bg-surface-brand-primary-light hover:text-text-hover bg-surface-white border',
        ghost: 'hover:bg-surface-neutral hover:text-text-primary',
        text: 'text-text-brand-primary hover:text-text-hover underline underline-offset-4',
        tertiary:
          'border-border-divider text-text-secondary bg-surface-white hover:bg-surface-neutral hover:border-border-enabled border',
        'tertiary-selected': 'border-border-active bg-surface-neutral text-text-selected text-text-primary border',
        'danger-primary': 'bg-surface-error hover:bg-surface-error-hover text-white',
        'danger-tertiary':
          'border-border-error text-text-error bg-surface-primary hover:bg-surface-error-disabled hover:border-border-error-hover hover:text-text-error-hover border',
      },
      size: {
        lg: 'px-4 text-lg min-h-[44px]',
        sm: 'min-h-4 px-2 text-base',
        // TODO: Remove after codemod migration - these are aliases for backwards compatibility
        default: 'px-4 text-base min-h-[44px]',
        md: 'px-4 text-base min-h-[44px]',
        icon: 'px-2 text-sm',
      },
      width: {
        auto: '',
        fit: 'w-fit',
        full: 'w-full',
        responsive: 'md:w-fit w-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'lg',
      width: 'auto',
    },
  },
)

export interface ButtonProps extends React.AriaAttributes, VariantProps<typeof buttonVariants> {
  onClick?: React.MouseEventHandler<HTMLButtonElement>
  disabled?: boolean
  type?: 'submit' | 'reset' | 'button'
  form?: string
  className?: string
  id?: string
  tabIndex?: number
  autoFocus?: boolean
  role?: React.AriaRole
  children?: React.ReactNode
  asChild?: boolean
  icon?: React.ReactNode
  iconPosition?: 'left' | 'right'
  text?: string
  [key: `data-${string}`]: string | undefined
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, width, icon, iconPosition, asChild = false, text, children, ...props }, ref) => {
    const Comp = (asChild ? Slot : 'button') as React.ElementType
    const hasIcon = Boolean(icon)
    const content = text ?? children
    const iconElement = hasIcon ? (
      <span className="size-1.5 flex shrink-0 items-center justify-center [&>svg]:size-full">{icon}</span>
    ) : null

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, width, className }), hasIcon && 'gap-1')}
        ref={ref}
        {...props}
      >
        {asChild ? (
          content
        ) : (
          <>
            {iconElement && iconPosition !== 'right' && iconElement}
            {content}
            {iconElement && iconPosition === 'right' && iconElement}
          </>
        )}
      </Comp>
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
